import React, { useState, useEffect } from 'react';
// Importations individuelles de Material UI pour éviter les problèmes de types
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { SelectChangeEvent } from '@mui/material/Select';
import { User, UserRole } from '../../types/user';

interface UserFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (user: Partial<User>) => void;
  title: string;
  user: User | null;
}

const UserFormDialog: React.FC<UserFormDialogProps> = ({ 
  open, 
  onClose, 
  onSubmit, 
  title, 
  user 
}) => {
  const [formData, setFormData] = useState<Partial<User>>({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    role: UserRole.USER,
    status: 'active',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Remplir le formulaire avec les données de l'utilisateur si en mode édition
  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        role: user.role,
        status: user.status,
        createdAt: user.createdAt,
        updatedAt: user.updatedAt,
        // Ne pas inclure le mot de passe en mode édition
      });
    } else {
      // Réinitialiser le formulaire en mode création
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        role: UserRole.USER,
        status: 'active',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
    }
  }, [user, open]);
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name) {
      // Traitement spécial pour les dates
      if (name === 'createdAt' || name === 'updatedAt') {
        return; // Géré par les gestionnaires d'événements spécifiques
      }
      
      setFormData({
        ...formData,
        [name]: value,
      });
      
      // Effacer l'erreur pour ce champ s'il y en a une
      if (errors[name]) {
        setErrors({
          ...errors,
          [name]: '',
        });
      }
    }
  };
  
  
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.firstName) {
      newErrors.firstName = 'Le prénom est requis';
    }
    
    if (!formData.lastName) {
      newErrors.lastName = 'Le nom est requis';
    }
    
    if (!formData.email) {
      newErrors.email = 'L\'email est requis';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Format d\'email invalide';
    }
    
    // Vérifier le mot de passe uniquement en mode création
    if (!user && !formData.password) {
      newErrors.password = 'Le mot de passe est requis';
    } else if (!user && formData.password && formData.password.length < 6) {
      newErrors.password = 'Le mot de passe doit contenir au moins 6 caractères';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async () => {
    if (validateForm()) {
      setIsSubmitting(true);
      
      try {
        // Nous laissons la page parent (AdminPage) gérer la création/mise à jour
        // pour éviter la double création d'utilisateurs
        onSubmit(formData);
        onClose();
      } catch (error) {
        console.error('Erreur lors de la soumission du formulaire:', error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };
  
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box component="form" sx={{ mt: 1 }}>
          <TextField
            margin="normal"
            required
            fullWidth
            id="firstName"
            label="Prénom"
            name="firstName"
            value={formData.firstName}
            onChange={handleInputChange}
            error={!!errors.firstName}
            helperText={errors.firstName}
            autoFocus
          />
          
          <TextField
            margin="normal"
            required
            fullWidth
            id="lastName"
            label="Nom"
            name="lastName"
            value={formData.lastName}
            onChange={handleInputChange}
            error={!!errors.lastName}
            helperText={errors.lastName}
          />
          
          <TextField
            margin="normal"
            required
            fullWidth
            id="email"
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleInputChange}
            error={!!errors.email}
            helperText={errors.email}
          />
          
          {/* Afficher le champ mot de passe uniquement en mode création */}
          {!user && (
            <TextField
              margin="normal"
              required
              fullWidth
              id="password"
              label="Mot de passe"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleInputChange}
              error={!!errors.password}
              helperText={errors.password}
            />
          )}
          
          <FormControl fullWidth margin="normal">
            <InputLabel id="role-label">Rôle</InputLabel>
            <Select
              labelId="role-label"
              id="role"
              name="role"
              value={formData.role || ''}
              label="Rôle"
              onChange={(e) => {
                const value = e.target.value as UserRole;
                setFormData({
                  ...formData,
                  role: value,
                });
              }}
            >
              <MenuItem value={UserRole.USER}>Utilisateur</MenuItem>
              <MenuItem value={UserRole.COACH}>Coach</MenuItem>
              <MenuItem value={UserRole.ADMIN}>Administrateur</MenuItem>
            </Select>
          </FormControl>
          
          <FormControl fullWidth margin="normal">
            <InputLabel id="status-label">Statut</InputLabel>
            <Select
              labelId="status-label"
              id="status"
              name="status"
              value={formData.status || ''}
              label="Statut"
              onChange={(e) => {
                const value = e.target.value as 'active' | 'suspended';
                setFormData({
                  ...formData,
                  status: value,
                });
              }}
            >
              <MenuItem value="active">Actif</MenuItem>
              <MenuItem value="suspended">Suspendu</MenuItem>
            </Select>
          </FormControl>
          
          <TextField
            margin="normal"
            fullWidth
            id="createdAt"
            label="Date de création"
            name="createdAt"
            type="date"
            value={formData.createdAt ? new Date(formData.createdAt).toISOString().split('T')[0] : ''}
            onChange={(e) => {
              const newDate = e.target.value;
              if (newDate) {
                // Créer une date à midi pour éviter les problèmes de fuseau horaire
                const date = new Date(newDate);
                date.setHours(12, 0, 0, 0);
                
                setFormData({
                  ...formData,
                  createdAt: date.toISOString()
                });
              }
            }}
            InputLabelProps={{
              shrink: true,
            }}
          />
          
          <TextField
            margin="normal"
            fullWidth
            id="updatedAt"
            label="Date de mise à jour"
            name="updatedAt"
            type="date"
            value={formData.updatedAt ? new Date(formData.updatedAt).toISOString().split('T')[0] : ''}
            onChange={(e) => {
              const newDate = e.target.value;
              if (newDate) {
                // Créer une date à midi pour éviter les problèmes de fuseau horaire
                const date = new Date(newDate);
                date.setHours(12, 0, 0, 0);
                
                setFormData({
                  ...formData,
                  updatedAt: date.toISOString()
                });
              }
            }}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          Annuler
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          color="primary"
          disabled={isSubmitting}
        >
          {isSubmitting ? <CircularProgress size={24} /> : user ? 'Mettre à jour' : 'Créer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserFormDialog;
